import {
  Component,
  Input,
  OnDestroy,
  SimpleChanges,
  ComponentFactoryResolver,
  ComponentFactory,
  Injector,
  ViewChild,
  ElementRef,
  OnChanges,
  OnInit,
  HostListener,
  Output,
  EventEmitter,
} from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { Subject } from 'rxjs';

import { MAPBOX_ACCESS_TOKEN } from '@app-request-video/constants/request-video.constants';
import { MapTooltipComponent } from '@app-live-view/components/map-tooltip/map-tooltip.component';
import { TripAssetDetails } from '@app-live-view/models/live-view.model';
import { DataService } from '@app-core/services/data/data.service';
import { takeUntil } from 'rxjs/operators';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

@Component({
  selector: 'app-clustering-mapbox',
  templateUrl: './clustering-mapbox.component.html',
  styleUrls: ['./clustering-mapbox.component.scss'],
})
export class ClusteringMapboxComponent implements OnDestroy, OnInit, OnChanges {
  @Input() public clusteringAssetDetails: TripAssetDetails[];
  @Input() public mapWidth = '100vw';
  @Input() public loader: boolean = true;
  @Input() private recenterClusterMap: boolean = false;

  @Output()
  private assetClick = new EventEmitter<TripAssetDetails>();

  @ViewChild('mapContainer') mapContainer!: ElementRef;

  public currentTheme: string = 'light';
  public map!: mapboxgl.Map;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private assetClusteringData = {};
  private toolTipFactory: ComponentFactory<MapTooltipComponent>;
  private popup!: mapboxgl.Popup;
  private attributionControl!: mapboxgl.AttributionControl | null;

  constructor(private resolver: ComponentFactoryResolver, private injector: Injector, private dataService: DataService) {}

  public ngOnInit() {
    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });

    this.map = new mapboxgl.Map({
      container: 'cluster-view-map',
      style: 'mapbox://styles/mapbox/streets-v11',
      attributionControl: false,
    });
    // Add attribution control if screen width is above 1024px
    this.updateAttributionControl();
    // Add the navigation control to the map
    const nav = new mapboxgl.NavigationControl();
    this.map.addControl(nav, 'top-left');
    this.map.addControl(new mapboxgl.FullscreenControl(), 'top-left');
    this.loadImages();
    this.addClusterLayer();
  }

  // attribution control starts
  // Show and hide attribution control (© Mapbox © OpenStreetMap Improve this map) on the map based on the screen size
  updateAttributionControl(): void {
    const isDesktop = window.innerWidth > 1024; // Set breakpoint for laptop/desktop
    if (this.attributionControl) {
      this.map.removeControl(this.attributionControl);
    }

    if (isDesktop) {
      this.attributionControl = new mapboxgl.AttributionControl({ compact: false });
      this.map.addControl(this.attributionControl, 'bottom-right');
    } else {
      this.attributionControl = null;
    }
  }
  // Listen for window resize to dynamically update attribution control
  @HostListener('window:resize')
  onResize() {
    this.updateAttributionControl();
  }
  // attribution control ends

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.clusteringAssetDetails && changes.clusteringAssetDetails.currentValue) {
      setTimeout(() => {
        this.loadClusterMap();
      }, 0);
    }

    if (changes.mapWidth && changes.mapWidth.currentValue) {
      setTimeout(() => {
        // eslint-disable-next-line
        const { nativeElement: { style: { width: mapContainerElementWidth = 0 } = {} } = {} } = this.mapContainer || {};
        if (mapContainerElementWidth) {
          this.mapContainer.nativeElement.style.width = this.mapWidth;
          this.map?.resize();
        }
      }, 0);
    }

    if (changes.recenterClusterMap && changes.recenterClusterMap.currentValue && this.recenterClusterMap) {
      this.fitMapToMarkers();
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadClusterMap() {
    this.assetClusteringData = {
      type: 'FeatureCollection',
      features: (this.clusteringAssetDetails || []).map((item) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [item.lastKnownLocation.longitude, item.lastKnownLocation.latitude],
        },
        properties: {
          assetName: item.asset.assetName,
          assetId: item.asset.assetId,
          status: item.state,
          driverId: item.driverId,
          driverName: item.driverName,
          timestampUTC: item.lastDevicePingTimestampUTC,
          timestamp: item.lastDevicePingTimestampUTC,
        },
      })),
    };
    if (this.map.getSource('assetPoints')) {
      this.updateClusterLayer();
    } else {
      this.map.on('load', () => {
        this.updateClusterLayer();
      });
    }
  }

  private updateClusterLayer() {
    const assetPointsSource = this.map.getSource('assetPoints');
    assetPointsSource.setData({ data: this.assetClusteringData });
    this.fitMapToMarkers();
  }

  private addClusterLayer() {
    this.map.on('load', () => {
      this.map.addSource('assetPoints', {
        type: 'geojson',
        data: [],
        cluster: true,
        clusterMaxZoom: 15, // Max zoom to cluster points on
        clusterRadius: 50, // Radius of each cluster when clustering points
      });

      this.map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'assetPoints',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#272727', // Color for clusters with fewer points
            100,
            '#272727', // Color for medium-sized clusters
            750,
            '#272727', // Color for large clusters
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20, // Radius for small clusters
            100,
            30, // Radius for medium-sized clusters
            750,
            40, // Radius for large clusters
          ],
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
      });

      this.map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'assetPoints',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Bold'],
          'text-size': 14,
        },
        paint: {
          'text-color': '#FFFFFF', // Set text color to white
        },
      });

      // Add unclustered points layer
      this.map.addLayer({
        id: 'unclustered-points',
        type: 'symbol',
        source: 'assetPoints',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'status'], 'ACTIVE'],
            'active-icon',
            ['==', ['get', 'status'], 'INACTIVE'],
            'inactive-icon',
            ['==', ['get', 'status'], 'PARKED'],
            'parked-icon',
            'default-icon', // Fallback in case no status matches
          ],
          'icon-size': 0.7, // Adjust the icon size as needed
          'text-allow-overlap': true,
          'icon-allow-overlap': true,
        },
      });

      // Click event on clusters to scatter them
      this.map.on('click', 'clusters', (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        const clusterId = features[0].properties.cluster_id;
        this.map.getSource('assetPoints').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          this.map.easeTo({
            center: features[0].geometry.coordinates as mapboxgl.LngLatLike,
            zoom: zoom,
          });
        });
      });

      this.map.on('mouseenter', 'unclustered-points', (e: any) => {
        this.map.getCanvas().style.cursor = 'pointer';

        const coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure the popup doesn't appear off-screen
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        const assetDetails = this.clusteringAssetDetails.find(
          (assetDetails) => e.features[0].properties.assetId === assetDetails?.asset?.assetId
        );
        const template = this.getToolTipTemplate(assetDetails);
        const popupContent = template.innerHTML;
        this.popup = new mapboxgl.Popup({
          closeButton: false,
          focusAfterOpen: false,
          className: 'custom-mapbox-popup',
        });
        this.popup.setMaxWidth('400px');
        this.popup.setLngLat(coordinates);
        this.popup.setHTML(popupContent);
        this.popup.addTo(this.map);
      });

      // Remove popup on mouse leave
      this.map.on('mouseleave', 'unclustered-points', () => {
        this.map.getCanvas().style.cursor = '';
        if (this.popup) {
          this.popup.remove();
        }
      });

      // Handle click event on unclustered points (last node)
      this.map.on('click', 'unclustered-points', (e) => {
        const selectedAsset = e.features[0].properties;
        const selectedAssetId = selectedAsset.assetId;
        const selectedAssetDetails = this.clusteringAssetDetails.find((assetDetails) => selectedAssetId === assetDetails?.asset?.assetId);
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend(e?.features[0]?.geometry?.coordinates);

        this.map.fitBounds(bounds, {
          padding: 50,
          maxZoom: Math.max(15, this.map.getZoom()),
        });
        this.assetClick.emit(selectedAssetDetails);
      });
    });
  }

  // Function to load images for different statuses
  private loadImages(): void {
    this.map.loadImage('assets/common/parked_device.png', (error, image) => {
      if (error) throw error;
      this.map.addImage('parked-icon', image!);
    });
    this.map.loadImage('assets/common/active-device.png', (error, image) => {
      if (error) throw error;
      this.map.addImage('active-icon', image!);
    });

    this.map.loadImage('assets/common/week_signal_device.png', (error, image) => {
      if (error) throw error;
      this.map.addImage('inactive-icon', image!);
    });

    // Optionally, you can add a fallback image for any points without status
    this.map.loadImage('assets/common/parked_device.png', (error, image) => {
      if (error) throw error;
      this.map.addImage('default-icon', image!);
    });
  }

  public onRecenterBtnClick() {
    this.fitMapToMarkers();
  }

  private fitMapToMarkers() {
    if (!this.map) {
      return;
    }
    const source = this.map.getSource('assetPoints') as mapboxgl.GeoJSONSource;

    source.setData(this.assetClusteringData); // Set the data if dynamic

    source.once('data', () => {
      const bounds = new mapboxgl.LngLatBounds();

      // Iterate over each feature and extend the bounds
      source._data.features.forEach((feature: any) => {
        const coordinates = feature.geometry.coordinates;
        bounds.extend(coordinates);
      });

      // Fit the map to the bounds of the features
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds, {
          padding: 50, // Padding around the markers
          maxZoom: 15, // Optional max zoom to prevent zooming in too much
        });
      }
    });
  }

  private getToolTipTemplate(data: TripAssetDetails) {
    this.toolTipFactory = this.resolver.resolveComponentFactory(MapTooltipComponent);
    const component = this.toolTipFactory.create(this.injector);
    component.instance.data = data;
    component.changeDetectorRef.detectChanges();
    const element = component.location.nativeElement;
    return element;
  }

  public fitBounds() {
    const bounds = new mapboxgl.LngLatBounds();
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 100 });
    }
  }
}
